import React, { FC, useEffect, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Typography,
	Grid,
	TextField,
	IconButton,
	InputAdornment,
	Skeleton
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import WifiIcon from "@mui/icons-material/Wifi";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SignalWifi4BarIcon from "@mui/icons-material/SignalWifi4Bar";
import SignalWifi3BarIcon from "@mui/icons-material/SignalWifi3Bar";
import SignalWifi2BarIcon from "@mui/icons-material/SignalWifi2Bar";
import SignalWifi1BarIcon from "@mui/icons-material/SignalWifi1Bar";
import SignalWifi0BarIcon from "@mui/icons-material/SignalWifi0Bar";
import SecurityIcon from "@mui/icons-material/Security";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from "react-hook-form";
import ConfirmationDialog from "../../common/ConfirmationDialog";

interface WifiFormData {
	ssid: string;
	password: string;
}

interface WifiNetwork {
	ssid: string;
	signal: number;
	security: string;
	connected: boolean;
}

interface SavedNetwork {
	ssid: string;
	security: string;
	connected: boolean;
}

const WifiConfiguration: FC = () => {
	const [wifiNetworks, setWifiNetworks] = useState<WifiNetwork[]>([]);
	const [savedNetworks, setSavedNetworks] = useState<SavedNetwork[]>([]);
	const [scanning, setScanning] = useState<boolean>(false);
	const [connecting, setConnecting] = useState<boolean>(false);
	const [loadingSaved, setLoadingSaved] = useState<boolean>(true);
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [showDisconnectDialog, setShowDisconnectDialog] = useState<boolean>(false);
	const [selectedSsid, setSelectedSsid] = useState<string>("");

	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors, isSubmitting }
	} = useForm<WifiFormData>({
		defaultValues: {
			ssid: "",
			password: ""
		}
	});

	useEffect(() => {
		loadSavedNetworks();
	}, []);

	const loadSavedNetworks = async () => {
		try {
			setLoadingSaved(true);
			const response = await fetch(`http://${window.location.hostname}:81/api/wifi/networks`);
			const data = await response.json();

			if (response.ok) {
				setSavedNetworks(data.networks || []);
			} else {
				throw new Error(data.error || "Failed to load saved networks");
			}
		} catch (error) {
			console.error("Error loading saved networks:", error);
		} finally {
			setLoadingSaved(false);
		}
	};

	const scanWifiNetworks = async () => {
		try {
			setScanning(true);
			setWifiNetworks([]);

			const response = await fetch(`http://${window.location.hostname}:81/api/wifi/scan`);
			const data = await response.json();

			if (response.ok) {
				setWifiNetworks(data.networks || []);
			} else {
				throw new Error(data.error || "Failed to scan WiFi networks");
			}
		} catch (error) {
			console.error("Error scanning WiFi networks:", error);
			alert(error instanceof Error ? error.message : "Failed to scan WiFi networks");
		} finally {
			setScanning(false);
		}
	};

	const connectToWifi = (ssid: string) => {
		setValue("ssid", ssid);
		alert(`Please enter password for ${ssid}`);
	};

	const onSubmit = async (data: WifiFormData) => {
		try {
			setConnecting(true);

			if (!data.ssid.trim()) {
				alert("Please enter a network name");
				return;
			}

			const requestData = {
				ssid: data.ssid.trim(),
				password: data.password.trim()
			};

			const response = await fetch(`http://${window.location.hostname}:81/api/wifi/connect`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify(requestData)
			});

			const result = await response.json();

			if (response.ok) {
				alert(result.message || "Connected to WiFi successfully");
				// Reset form
				setValue("ssid", "");
				setValue("password", "");
				// Refresh networks after connection
				setTimeout(() => {
					scanWifiNetworks();
					loadSavedNetworks();
				}, 2000);
			} else {
				throw new Error(result.error || "Failed to connect to WiFi");
			}
		} catch (error) {
			console.error("Error connecting to WiFi:", error);
			alert(error instanceof Error ? error.message : "Failed to connect to WiFi");
		} finally {
			setConnecting(false);
		}
	};

	const disconnectWifi = async (ssid: string) => {
		setSelectedSsid(ssid);
		setShowDisconnectDialog(true);
	};

	const confirmDisconnectWifi = async () => {
		if (!selectedSsid) return;

		try {
			const response = await fetch(`http://${window.location.hostname}:81/api/wifi/disconnect`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify({ ssid: selectedSsid })
			});

			const result = await response.json();

			if (response.ok) {
				alert(result.message || "Disconnected from WiFi successfully");
				// Refresh networks after disconnection
				setTimeout(() => {
					scanWifiNetworks();
					loadSavedNetworks();
				}, 2000);
			} else {
				throw new Error(result.error || "Failed to disconnect from WiFi");
			}
		} catch (error) {
			console.error("Error disconnecting from WiFi:", error);
			alert(error instanceof Error ? error.message : "Failed to disconnect from WiFi");
		} finally {
			setShowDisconnectDialog(false);
			setSelectedSsid("");
		}
	};

	const getSignalIcon = (signal: number) => {
		if (signal >= 80) return <SignalWifi4BarIcon />;
		if (signal >= 60) return <SignalWifi3BarIcon />;
		if (signal >= 40) return <SignalWifi2BarIcon />;
		if (signal >= 20) return <SignalWifi1BarIcon />;
		return <SignalWifi0BarIcon />;
	};

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	return (
		<Box
			className="network-setting-section-wrapper"
			sx={{
				background: "#ffffff",
				borderRadius: "12px",
				boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
				border: "1px solid rgba(0, 0, 0, 0.06)"
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
				<Box>
					<Typography variant="h6" color="var(--color-primary-main)">
						WiFi Configuration
					</Typography>
					<Typography variant="body1" color="#616161">
						Scan, connect to, and manage WiFi networks
					</Typography>
				</Box>

				<Button
					variant="outlined"
					color="primary"
					startIcon={scanning ? <CircularProgress size={16} /> : <RefreshIcon />}
					onClick={scanWifiNetworks}
					disabled={scanning}
				>
					{scanning ? "Scanning..." : "Scan Networks"}
				</Button>
			</Box>

			<Box sx={{ width: "100%" }}>
				<Grid container spacing={2}>
					{/* Available Networks */}
					<Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
						<Box
							sx={{
								background: "#f8f9fa",
								borderRadius: "12px",
								padding: "24px",
								border: "1px solid #e9ecef",
								flex: 1,
								display: "flex",
								flexDirection: "column"
							}}
						>
							<Box sx={{ mb: 3 }}>
								<Typography
									variant="h6"
									color="var(--color-primary-main)"
									fontWeight={600}
									sx={{ fontSize: "20px", mb: 0.5 }}
								>
									Available Networks
								</Typography>
								<Typography variant="body2" color="#6c757d" sx={{ fontSize: "14px" }}>
									WiFi networks found in range
								</Typography>
							</Box>

							{wifiNetworks.length === 0 ? (
								<Box
									sx={{
										textAlign: "center",
										py: 4,
										color: "#6c757d",
										background: "#ffffff",
										borderRadius: "8px",
										border: "1px dashed #dee2e6",
										flex: 1,
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center"
									}}
								>
									{scanning ? (
										<CircularProgress size={38} color="primary" sx={{ mb: 2 }} />
									) : (
										<WifiIcon sx={{ fontSize: 48, color: "#dee2e6", mb: 2 }} />
									)}

									<Typography variant="body2" sx={{ fontSize: "14px" }}>
										{scanning ? "Scanning for WiFi networks..." : "Click 'Scan Networks' to find WiFi networks"}
									</Typography>
								</Box>
							) : (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
									{wifiNetworks.map((network, index) => (
										<Box
											key={index}
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												padding: "16px",
												background: "#ffffff",
												borderRadius: "10px",
												border: "1px solid #e9ecef",
												transition: "all 0.2s ease-in-out",
												"&:hover": {
													boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
													borderColor: "#dee2e6"
												}
											}}
										>
											<Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
												<Box sx={{ flex: 1 }}>
													<Typography
														variant="subtitle1"
														fontWeight={600}
														sx={{
															fontSize: "16px",
															color: "#212529",
															mb: 0.5
														}}
													>
														{network.ssid}
													</Typography>

													<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
														<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
															{getSignalIcon(network.signal)}
															<Typography
																variant="caption"
																color="#6c757d"
																sx={{
																	fontSize: "12px",
																	fontWeight: 500
																}}
															>
																{network.signal}%
															</Typography>
														</Box>

														<Box
															sx={{
																display: "inline-flex",
																alignItems: "center",
																padding: "4px 8px",
																borderRadius: "8px",
																background:
																	network.security && network.security !== "--"
																		? "rgba(46, 125, 50, 0.1)"
																		: "rgba(108, 117, 125, 0.1)",
																border: "1px solid",
																borderColor:
																	network.security && network.security !== "--"
																		? "rgba(46, 125, 50, 0.2)"
																		: "rgba(108, 117, 125, 0.2)",
																minWidth: "fit-content",
																gap: 0.5
															}}
														>
															<SecurityIcon
																sx={{
																	fontSize: "12px",
																	color: network.security && network.security !== "--" ? "#2e7d32" : "#6c757d"
																}}
															/>
															<Typography
																variant="caption"
																fontWeight={600}
																color={network.security && network.security !== "--" ? "#2e7d32" : "#6c757d"}
																sx={{
																	fontSize: "11px",
																	lineHeight: 1.2
																}}
															>
																{network.security}
															</Typography>
														</Box>
													</Box>
												</Box>
											</Box>

											<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
												{network.connected ? (
													<Button
														variant="outlined"
														size="small"
														startIcon={<CloseIcon sx={{ fontSize: "16px" }} />}
														onClick={() => disconnectWifi(network.ssid)}
														sx={{
															borderColor: "#fd7e14",
															color: "#fd7e14",
															textTransform: "none",
															fontWeight: 500,
															fontSize: "13px",
															padding: "6px 12px",
															borderRadius: "8px",
															minWidth: "auto",
															"&:hover": {
																borderColor: "#e55a00",
																backgroundColor: "rgba(253, 126, 20, 0.04)"
															}
														}}
													>
														Disconnect
													</Button>
												) : (
													<Button
														variant="contained"
														size="small"
														startIcon={<WifiIcon sx={{ fontSize: "16px" }} />}
														onClick={() => connectToWifi(network.ssid)}
														sx={{
															background: "var(--color-primary-main)",
															color: "#ffffff",
															textTransform: "none",
															fontWeight: 500,
															fontSize: "13px",
															padding: "6px 12px",
															borderRadius: "8px",
															minWidth: "auto",
															"&:hover": {
																background: "var(--color-primary-dark)"
															}
														}}
													>
														Connect
													</Button>
												)}
											</Box>
										</Box>
									))}
								</Box>
							)}
						</Box>
					</Grid>

					{/* Connect to Network */}
					<Grid item xs={12} md={6}>
						<Box
							component="form"
							noValidate
							onSubmit={handleSubmit(onSubmit)}
							sx={{ display: "flex", flexDirection: "column", gap: 2 }}
						>
							<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
								Connect to Network
							</Typography>

							<Box className="network-setting-wrapper">
								<Box className="network-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Network Name (SSID)
									</Typography>

									<Typography variant="body2" color="#616161">
										Enter the network name (SSID) to connect to
									</Typography>
								</Box>

								<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
									<Controller
										name="ssid"
										control={control}
										rules={{ required: "Network name is required" }}
										render={({ field }) => (
											<TextField
												{...field}
												fullWidth
												size="small"
												variant="filled"
												label="Network Name (SSID)"
												placeholder="Enter network name"
												error={!!errors.ssid}
												helperText={errors.ssid?.message}
												InputProps={{ disableUnderline: true, classes: { root: "filled-input-root" } }}
											/>
										)}
									/>
								</Box>
							</Box>

							<Box className="network-setting-wrapper">
								<Box className="network-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Password
									</Typography>

									<Typography variant="body2" color="#616161">
										Enter the password for the network
									</Typography>
								</Box>

								<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
									<Controller
										name="password"
										control={control}
										render={({ field }) => (
											<TextField
												{...field}
												fullWidth
												size="small"
												variant="filled"
												label="Password"
												type={showPassword ? "text" : "password"}
												placeholder="Enter password (leave empty for open networks)"
												error={!!errors.password}
												helperText={errors.password?.message}
												InputProps={{
													disableUnderline: true,
													classes: { root: "filled-input-root" },
													endAdornment: (
														<InputAdornment position="end">
															<IconButton onClick={togglePasswordVisibility} edge="end">
																{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
															</IconButton>
														</InputAdornment>
													)
												}}
											/>
										)}
									/>
								</Box>
							</Box>

							<Box sx={{ display: "flex" }}>
								<Button
									type="submit"
									variant="contained"
									color="primary"
									startIcon={connecting ? <CircularProgress size={16} /> : <WifiIcon />}
									disabled={connecting || isSubmitting}
								>
									{connecting ? "Connecting..." : "Connect"}
								</Button>
							</Box>
						</Box>

						{/* Saved Networks */}
						<Box sx={{ mt: 4 }}>
							<Box
								sx={{
									background: "#f8f9fa",
									borderRadius: "12px",
									padding: "24px",
									border: "1px solid #e9ecef"
								}}
							>
								<Box sx={{ mb: 3 }}>
									<Typography
										variant="h6"
										color="var(--color-primary-main)"
										fontWeight={600}
										sx={{ fontSize: "20px", mb: 0.5 }}
									>
										Saved Networks
									</Typography>
									<Typography variant="body2" color="#6c757d" sx={{ fontSize: "14px" }}>
										Previously connected networks
									</Typography>
								</Box>

								{loadingSaved ? (
									<Box sx={{ width: "100%" }}>
										{/* Network skeleton items */}
										{[1, 2, 3].map((index) => (
											<Box
												key={index}
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "space-between",
													padding: "16px",
													background: "#ffffff",
													borderRadius: "10px",
													border: "1px solid #e9ecef",
													mb: 2
												}}
											>
												<Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
													{/* Security icon skeleton */}
													<Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: "8px" }} />

													<Box sx={{ flex: 1 }}>
														{/* SSID skeleton */}
														<Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
														{/* Status badge skeleton */}
														<Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: "12px" }} />
													</Box>
												</Box>

												{/* Button skeleton */}
												<Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: "8px" }} />
											</Box>
										))}
									</Box>
								) : savedNetworks.length === 0 ? (
									<Box
										sx={{
											textAlign: "center",
											py: 4,
											color: "#6c757d",
											background: "#ffffff",
											borderRadius: "8px",
											border: "1px dashed #dee2e6"
										}}
									>
										<WifiIcon sx={{ fontSize: 48, color: "#dee2e6", mb: 2 }} />
										<Typography variant="body2" sx={{ fontSize: "14px" }}>
											No saved networks found
										</Typography>
									</Box>
								) : (
									<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
										{savedNetworks.map((network, index) => (
											<Box
												key={index}
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "space-between",
													padding: "16px",
													background: "#ffffff",
													borderRadius: "10px",
													border: "1px solid #e9ecef",
													transition: "all 0.2s ease-in-out",
													"&:hover": {
														boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
														borderColor: "#dee2e6"
													}
												}}
											>
												<Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															width: "40px",
															height: "40px",
															borderRadius: "8px",
															background:
																network.security && network.security !== "--"
																	? "rgba(46, 125, 50, 0.1)"
																	: "rgba(108, 117, 125, 0.1)",
															border: "1px solid",
															borderColor:
																network.security && network.security !== "--"
																	? "rgba(46, 125, 50, 0.2)"
																	: "rgba(108, 117, 125, 0.2)"
														}}
													>
														<SecurityIcon
															sx={{
																fontSize: "20px",
																color: network.security && network.security !== "--" ? "#2e7d32" : "#6c757d"
															}}
														/>
													</Box>

													<Box sx={{ flex: 1 }}>
														<Typography
															variant="subtitle1"
															fontWeight={600}
															sx={{
																fontSize: "16px",
																color: "#212529",
																mb: 0.5
															}}
														>
															{network.ssid}
														</Typography>

														<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
															{network.connected && (
																<Box
																	sx={{
																		display: "inline-flex",
																		alignItems: "center",
																		padding: "4px 8px",
																		borderRadius: "12px",
																		background: "#d4edda",
																		border: "1px solid #c3e6cb",
																		minWidth: "fit-content"
																	}}
																>
																	<Typography
																		variant="caption"
																		fontWeight={600}
																		color="#155724"
																		sx={{
																			fontSize: "11px",
																			lineHeight: 1.2,
																			textTransform: "uppercase",
																			letterSpacing: "0.5px"
																		}}
																	>
																		Connected
																	</Typography>
																</Box>
															)}
														</Box>
													</Box>
												</Box>

												<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
													{network.connected ? (
														<Button
															variant="outlined"
															size="small"
															startIcon={<CloseIcon sx={{ fontSize: "16px" }} />}
															onClick={() => disconnectWifi(network.ssid)}
															sx={{
																borderColor: "#fd7e14",
																color: "#fd7e14",
																textTransform: "none",
																fontWeight: 500,
																fontSize: "13px",
																padding: "6px 12px",
																borderRadius: "8px",
																minWidth: "auto",
																"&:hover": {
																	borderColor: "#e55a00",
																	backgroundColor: "rgba(253, 126, 20, 0.04)"
																}
															}}
														>
															Disconnect
														</Button>
													) : (
														<Button
															variant="contained"
															size="small"
															startIcon={<WifiIcon sx={{ fontSize: "16px" }} />}
															onClick={() => connectToWifi(network.ssid)}
															sx={{
																background: "var(--color-primary-main)",
																color: "#ffffff",
																textTransform: "none",
																fontWeight: 500,
																fontSize: "13px",
																padding: "6px 12px",
																borderRadius: "8px",
																minWidth: "auto",
																"&:hover": {
																	background: "var(--color-primary-dark)"
																}
															}}
														>
															Connect
														</Button>
													)}
												</Box>
											</Box>
										))}
									</Box>
								)}
							</Box>
						</Box>
					</Grid>
				</Grid>
			</Box>

			<ConfirmationDialog
				width="sm"
				open={showDisconnectDialog}
				title="Disconnect WiFi"
				heading={`Are you sure you want to disconnect from ${selectedSsid}?`}
				onClose={() => {
					setShowDisconnectDialog(false);
					setSelectedSsid("");
				}}
				onConfirm={confirmDisconnectWifi}
			/>
		</Box>
	);
};

export default WifiConfiguration;
